import React, { useState, useCallback, memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Divider
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import RefreshIcon from '@material-ui/icons/Refresh';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import DateRangeIcon from '@material-ui/icons/DateRange';
import TodayIcon from '@material-ui/icons/Today';
import { i18n } from "../../translate/i18n";
import moment from 'moment';
import { useDashboardContext } from '../../context/Dashboard/DashboardContext';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.borderRadius?.medium || 12,
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 12px rgba(0, 0, 0, 0.35)'
      : theme.shadows[1],
    border: theme.palette.type === 'dark'
      ? `1px solid ${theme.palette.divider}`
      : 'none',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dateInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  iconButton: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.03)',
    boxShadow: 'none',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.06)',
      boxShadow: theme.shadows[1],
    },
    transition: 'all 0.2s',
  },
  refreshing: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(25, 118, 210, 0.15)'
      : 'rgba(25, 118, 210, 0.08)',
  },
  divider: {
    height: 24,
    margin: theme.spacing(0, 1),
  },
  chip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 500,
  },
  spinnerContainer: {
    position: 'relative',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  }
}));

const RefreshButton = ({ onClick, loading }) => {
  const classes = useStyles();

  return (
    <Tooltip title={i18n.t("dashboard.refresh") || "Atualizar dados"}>
      <div className={classes.spinnerContainer}>
        <IconButton
          className={`${classes.iconButton} ${loading ? classes.refreshing : ''}`}
          size="small"
          onClick={onClick}
          color="primary"
          disabled={loading}
        >
          <RefreshIcon
            style={{
              animation: loading ? 'spin 1s linear infinite' : 'none'
            }}
          />
        </IconButton>
      </div>
    </Tooltip>
  );
};

const DashboardHeader = memo(({
  isFullscreen,
  toggleFullscreen,
  handleRefresh,
  dateStartTicket,
  dateEndTicket,
  loading
}) => {
  const classes = useStyles();
  const [refreshing, setRefreshing] = useState(false);
  const { toggleFilterVisibility } = useDashboardContext();

  const getDateRangeText = useCallback(() => {
    if (!dateStartTicket || !dateEndTicket) return "Período não selecionado";

    const start = moment(dateStartTicket);
    const end = moment(dateEndTicket);

    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      if (start.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
        return `Hoje (${start.format('DD/MM/YYYY')})`;
      }
      return `${start.format('DD/MM/YYYY')}`;
    }

    return `${start.format('DD/MM/YYYY')} até ${end.format('DD/MM/YYYY')}`;
  }, [dateStartTicket, dateEndTicket]);

  const handleToggleFullscreen = useCallback(() => {
    window.requestAnimationFrame(() => {
      setTimeout(() => {
        toggleFullscreen();
      }, 0);
    });
  }, [toggleFullscreen]);

  const handleRefreshClick = useCallback(async () => {
    if (refreshing || loading) return;

    setRefreshing(true);
    try {
      await handleRefresh();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 500);
    }
  }, [refreshing, loading, handleRefresh]);

  const getChipForQuickFilter = useCallback(() => {
    const today = moment().format('YYYY-MM-DD');
    const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
    const startOfThisWeek = moment().startOf('week').format('YYYY-MM-DD');
    const endOfThisWeek = moment().endOf('week').format('YYYY-MM-DD');
    const startOfThisMonth = moment().startOf('month').format('YYYY-MM-DD');

    if (dateStartTicket === today && dateEndTicket === today) {
      return { label: "Hoje", icon: <TodayIcon fontSize="small" style={{ marginRight: 4 }} /> };
    }

    if (dateStartTicket === yesterday && dateEndTicket === yesterday) {
      return { label: "Ontem" };
    }

    if (dateStartTicket === startOfThisWeek && dateEndTicket === endOfThisWeek) {
      return { label: "Esta Semana" };
    }

    if (dateStartTicket === startOfThisMonth && dateEndTicket === today) {
      return { label: "Este Mês" };
    }

    if (dateStartTicket === moment().subtract(6, 'days').format('YYYY-MM-DD') && dateEndTicket === today) {
      return { label: "Últimos 7 dias" };
    }

    return null;
  }, [dateStartTicket, dateEndTicket]);

  const quickFilter = getChipForQuickFilter();

  return (
    <Box className={classes.header}>
      <div className={classes.leftSection}>
        <div className={classes.dateInfo}>
          <DateRangeIcon fontSize="small" />
          <Typography variant="body2">
            {getDateRangeText()}
          </Typography>
        </div>
        {quickFilter && (
          <Chip
            size="small"
            icon={quickFilter.icon}
            label={quickFilter.label}
            className={classes.chip}
            onClick={toggleFilterVisibility}
          />
        )}
      </div>

      <div className={classes.actions}>
        <Tooltip title={i18n.t("dashboard.actions.filter") || "Filtrar"}>
          <IconButton
            className={classes.iconButton}
            size="small"
            onClick={toggleFilterVisibility}
            color="primary"
          >
            <FilterListIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" className={classes.divider} />

        <RefreshButton onClick={handleRefreshClick} loading={refreshing || loading} />

        <Tooltip title={
          isFullscreen
            ? (i18n.t("dashboard.actions.exitFullscreen") || "Sair da tela cheia")
            : (i18n.t("dashboard.actions.fullscreen") || "Tela cheia")
        }>
          <IconButton
            className={classes.iconButton}
            size="small"
            onClick={handleToggleFullscreen}
            color="primary"
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </div>
    </Box>
  );
});

export default DashboardHeader; 